import React, { useState } from 'react';
import { Mic, MicOff, PhoneOff, MonitorUp, MonitorOff, Copy, Check, CreditCard } from 'lucide-react';
import { useSharedCart } from '@/components/shared-cart/SharedCartProvider';
import { useUserStore } from '@/stores/userStore';
import type { PartyRTCState } from './usePartyRTC';

interface PartyBarProps {
  rtc: PartyRTCState;
  inviteCode: string | null;
  onCheckout: () => void;
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .map(w => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

// The party bar: who's here, who's talking, and the session controls.
// Modeled on a game voice lobby — presence and voice state live on the
// avatars themselves, not in a separate settings panel.
export function PartyBar({ rtc, inviteCode, onCheckout }: PartyBarProps) {
  const { sharedCart, onlineParticipants, isConnected } = useSharedCart();
  const myId = useUserStore(s => s.user?.id) || '';
  const [copied, setCopied] = useState(false);

  if (!sharedCart) return null;

  const activeParticipants = sharedCart.participants.filter(p => p.status === 'active');

  const copyCode = async () => {
    if (!inviteCode) return;
    await navigator.clipboard.writeText(inviteCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  return (
    <div className="flex flex-wrap items-center gap-4 rounded-xl bg-den px-4 py-3">
      {/* Roster */}
      <div className="flex items-center -space-x-1.5">
        {activeParticipants.map(p => {
          const id = p.userId || p.guestId || '';
          const online = id === myId || onlineParticipants.has(id);
          const inVoice = rtc.voiceMembers.includes(id);
          const isSpeaking = !!rtc.speaking[id];
          return (
            <div key={p.id} className="relative" title={`${p.displayName}${inVoice ? ' · in voice' : ''}`}>
              <div
                className={`flex h-9 w-9 items-center justify-center rounded-full border-2 border-den font-display text-xs font-bold text-white ${
                  online ? 'bg-coral' : 'bg-denline'
                } ${isSpeaking ? 'animate-speakring' : ''}`}
              >
                {initials(p.displayName)}
              </div>
              {inVoice && (
                <span className="absolute -bottom-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-radio">
                  <Mic className="h-2.5 w-2.5 text-white" />
                </span>
              )}
            </div>
          );
        })}
      </div>

      <div className="h-6 w-px bg-denline" aria-hidden />

      {/* Voice controls */}
      {!rtc.inVoice ? (
        <button
          onClick={() => rtc.joinVoice().catch(err => console.error(err))}
          className="btn-den"
          disabled={!isConnected}
          title={isConnected ? 'Join voice chat' : 'Connecting to the session…'}
        >
          <Mic className="h-4 w-4" /> Join voice
        </button>
      ) : (
        <>
          <button
            onClick={rtc.toggleMute}
            className={`btn-den ${rtc.muted ? 'text-coral' : ''}`}
            aria-pressed={rtc.muted}
          >
            {rtc.muted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
            {rtc.muted ? 'Unmute' : 'Mute'}
          </button>
          <button onClick={rtc.leaveVoice} className="btn-den text-coral">
            <PhoneOff className="h-4 w-4" /> Leave voice
          </button>
        </>
      )}

      {/* Screen share */}
      {!rtc.isSharingScreen ? (
        <button
          onClick={() => rtc.startScreenShare().catch(err => console.error(err))}
          className="btn-den"
          disabled={rtc.presenterId !== null && rtc.presenterId !== myId}
          title={rtc.presenterId && rtc.presenterId !== myId ? 'Someone else is presenting' : 'Share your screen'}
        >
          <MonitorUp className="h-4 w-4" /> Share screen
        </button>
      ) : (
        <button onClick={rtc.stopScreenShare} className="btn-den text-coral">
          <MonitorOff className="h-4 w-4" /> Stop sharing
        </button>
      )}

      <div className="ml-auto flex items-center gap-3">
        {/* Party ticket */}
        {inviteCode && (
          <button onClick={copyCode} className="party-ticket" title="Copy party code">
            <span className="text-xs uppercase tracking-wide text-white/50">Party code</span>
            <code>{inviteCode}</code>
            {copied ? <Check className="h-4 w-4 text-aisle" /> : <Copy className="h-4 w-4 text-white/50" />}
          </button>
        )}

        <button onClick={onCheckout} className="btn-primary">
          <CreditCard className="h-4 w-4" /> Check out together
        </button>
      </div>
    </div>
  );
}
